<template>
  <!--
    ⚠️ ทุกข้อความบนหน้านี้ต้องผ่าน $t() เท่านั้น · ภาษาเริ่มต้นของระบบคือ 'en'
       (plugins/i18n.js) — ข้อความไทยที่ฮาร์ดโค้ดไว้จะโผล่บนหน้าจอภาษาอังกฤษทันที
       ซึ่งเป็นบั๊กที่ทำให้หน้านี้ถูกแก้ทั้งหน้า · คีย์ทุกตัวต้องมีครบทั้ง th และ en
       (คีย์ที่ขาดข้างหนึ่งจะ fallback กลับไปเป็นไทยแบบเงียบ ๆ ไม่มี error ให้ใครเห็น)
    ⚠️ NavBar.vue ยัง**ฮาร์ดโค้ด**ชื่อเมนู 'งานเบื้องหลัง QA' อยู่ (บรรทัด ~3206) —
       ตอนนี้คีย์ `_QAJobs` มีค่าแล้วทั้งสองภาษา เปลี่ยนตรงนั้นเป็น this.$t('_QAJobs')
       ได้เลย ไม่งั้นเมนูจะเป็นไทยอยู่หน้าเดียวทั้งที่หน้าจอเป็นอังกฤษ
  -->
  <QaPageShell :title="$t('_QAJobs')" :loading="loading && !result" :error="error" @retry="load">
    <template #toolbar>
      <span class="text-caption mr-3 d-none d-sm-inline">{{ lastUpdatedText }}</span>
      <v-btn
        variant="text"
        color="white"
        size="small"
        :prepend-icon="autoRefresh ? 'mdi-pause' : 'mdi-play'"
        @click="autoRefresh = !autoRefresh"
      >
        {{ autoRefresh ? $t('qaJobsAutoOn', { n: refreshSeconds }) : $t('qaJobsAutoOff') }}
      </v-btn>
      <v-btn variant="text" color="white" size="small" prepend-icon="mdi-refresh" :loading="inFlight" @click="load(true)">
        {{ $t('qaJobsRefresh') }}
      </v-btn>
    </template>

    <!--
      อ่านรอบล่าสุดไม่สำเร็จ แต่ยังมีของเดิมค้างอยู่บนจอ — ต้องบอกว่าที่เห็นคือของเก่า
      ⚠️ ห้ามล้างหน้าจอทิ้ง · หน้านี้ถูก poll ทุกไม่กี่วินาที การกระพริบเป็นหน้าแดงทุกครั้ง
         ที่เน็ตสะดุดหนึ่งรอบ จะทำให้คนเลิกเชื่อหน้าจอนี้ทั้งหน้า
    -->
    <v-alert v-if="staleError" type="warning" variant="tonal" density="compact" class="mt-3" icon="mdi-clock-alert-outline">
      <span class="text-body-2">
        {{ $t('qaJobsStaleBanner', { message: staleError.message, when: lastUpdatedRelative }) }}
      </span>
    </v-alert>

    <!-- ══ อ่านสถานะไม่ได้ — แยกสาเหตุทีละแบบ ไม่ใช่กล่องแดงกล่องเดียว ══════════════ -->
    <v-card v-if="result && !result.isOk" class="qa-card mt-3" variant="flat">
      <div class="pa-5 d-flex ga-4 align-start">
        <v-avatar :color="outcomeCard.tone" size="42" variant="tonal" rounded="lg">
          <v-icon :color="outcomeCard.tone" size="24">{{ outcomeCard.icon }}</v-icon>
        </v-avatar>
        <div class="flex-grow-1">
          <div class="text-h6 font-weight-medium" :class="`text-${outcomeCard.tone}`">{{ outcomeCard.title }}</div>
          <div class="text-body-2 mt-1">{{ outcomeCard.lead }}</div>

          <!--
            ข้อความจาก backend — เป็นประโยคที่ฝั่ง server แต่งมาแล้ว เอามาแสดงตรง ๆ
            ⚠️ ยังเป็นภาษาไทยจากฝั่ง Chat Framework · ไม่มีคีย์ให้แปลตรงนี้ (แปลไม่ได้
               เพราะเป็นข้อความอิสระ) — ถ้าจะให้เป็นอังกฤษด้วย ต้องแก้ที่ฝั่ง server
          -->
          <div v-if="result.detail" class="text-body-2 text-medium-emphasis mt-2">{{ result.detail }}</div>

          <div class="mt-4">
            <div class="text-subtitle-2 mb-1">{{ $t('qaJobsWhatToDo') }}</div>
            <ul class="qa-todo text-body-2">
              <li v-for="(step, i) in outcomeCard.todo" :key="i">{{ step }}</li>
            </ul>
          </div>

          <v-divider class="my-4" />
          <div class="text-caption text-medium-emphasis">
            <div v-if="result.endpoint">{{ $t('qaJobsEndpointLabel') }} <code class="qa-code">{{ result.endpoint }}</code></div>
            <div v-else>{{ $t('qaJobsNoRequestSent') }}</div>
            <div v-if="result.httpStatusCode">{{ $t('qaJobsHttpReceived', { code: result.httpStatusCode }) }}</div>
          </div>
        </div>
      </div>
    </v-card>

    <!-- ══ อ่านได้ — สามเรื่องที่อธิบายว่า "ทำไมไม่มีอะไรเดิน" ═══════════════════════ -->
    <template v-if="result && result.isOk">
      <!--
        คำตัดสินรวม — ของสำคัญที่สุดของหน้านี้
        ⚠️ "สวิตช์เปิดแต่ทุกงานว่าง" มีสองสาเหตุที่ต่างกันคนละเรื่อง: ไม่มีของให้ทำ
           กับ worker ถูกกลั้นไว้เพราะการตรวจ LLM ยังไม่ผ่าน · สองอันนี้ต้องแยกออกจากกัน
           ตั้งแต่บรรทัดแรกที่คนอ่าน ไม่ใช่ให้ไปเดาเอาจากตารางข้างล่าง
      -->
      <v-card class="qa-card mt-3" variant="flat">
        <div class="pa-5 d-flex ga-4 align-start">
          <v-avatar :color="verdict.tone" size="42" variant="tonal" rounded="lg">
            <v-icon :color="verdict.tone" size="24">{{ verdict.icon }}</v-icon>
          </v-avatar>
          <div class="flex-grow-1">
            <div class="text-h6 font-weight-medium" :class="`text-${verdict.tone}`">{{ verdict.title }}</div>
            <div class="text-body-2 mt-1">{{ verdict.body }}</div>
            <div v-if="verdict.hint" class="text-body-2 text-medium-emphasis mt-2">{{ verdict.hint }}</div>
          </div>
        </div>

        <v-divider />

        <v-row dense class="pa-4">
          <!-- 1. สวิตช์ใหญ่ -->
          <v-col cols="12" md="4">
            <div class="qa-card-sm pa-4 h-100">
              <div class="d-flex align-start justify-space-between">
                <div class="text-caption text-medium-emphasis">{{ $t('qaJobsMasterSwitch') }}</div>
                <v-icon size="18" :color="status.jobsEnabled ? 'success' : 'error'">
                  {{ status.jobsEnabled ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off-outline' }}
                </v-icon>
              </div>
              <div class="text-h6 font-weight-medium mt-2" :class="status.jobsEnabled ? 'text-success' : 'text-error'">
                {{ status.jobsEnabled ? $t('qaJobsSwitchOn') : $t('qaJobsSwitchOff') }}
              </div>
              <div class="text-caption text-medium-emphasis mt-1">
                {{ $t('qaJobsRegisteredCount', { n: registeredCount, total: jobs.length }) }}
              </div>
            </div>
          </v-col>

          <!-- 2. การตรวจ LLM + การกลั้นงาน — ใบที่คนอ่านผิดบ่อยที่สุด -->
          <v-col cols="12" md="4">
            <div class="qa-card-sm pa-4 h-100">
              <div class="d-flex align-start justify-space-between">
                <div class="text-caption text-medium-emphasis">{{ $t('qaJobsSelfTestCard') }}</div>
                <v-icon size="18" :color="selfTest.tone">{{ selfTest.icon }}</v-icon>
              </div>
              <div class="text-h6 font-weight-medium mt-2" :class="`text-${selfTest.tone}`">{{ selfTest.label }}</div>
              <div class="text-caption mt-1" :class="status.jobsHeldByLlmSelfTest ? 'text-error font-weight-medium' : 'text-medium-emphasis'">
                {{ status.jobsHeldByLlmSelfTest ? $t('qaJobsHeldNote') : $t('qaJobsNotHeldNote') }}
              </div>
              <div v-if="status.llmSelfTestFailedAttempts > 0" class="text-caption text-medium-emphasis mt-1">
                {{ $t('qaJobsSelfTestFailedAttempts', { n: status.llmSelfTestFailedAttempts }) }}
              </div>
            </div>
          </v-col>

          <!-- 3. เดินอยู่จริงกี่ตัวตอนนี้ -->
          <v-col cols="12" md="4">
            <div class="qa-card-sm pa-4 h-100">
              <div class="d-flex align-start justify-space-between">
                <div class="text-caption text-medium-emphasis">{{ $t('qaJobsRunningNow') }}</div>
                <v-icon size="18" :color="runningJobs.length ? 'success' : 'grey'">mdi-cog-play-outline</v-icon>
              </div>
              <div class="text-h6 font-weight-medium mt-2 qa-num">
                {{ $t('qaJobsCountOfTotal', { n: runningJobs.length, total: jobs.length }) }}
              </div>
              <div class="text-caption text-medium-emphasis mt-1">
                {{ runningJobs.length ? runningJobs.map((j) => j.label).join(' · ') : $t('qaJobsNoneRunning') }}
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- เหตุผลที่การตรวจ LLM ไม่ผ่าน — ข้อความจากฝั่ง middleware ห้ามตัดทิ้ง -->
        <v-alert
          v-if="status.llmSelfTestReason"
          :type="status.jobsHeldByLlmSelfTest ? 'error' : 'warning'"
          variant="tonal"
          density="compact"
          class="mx-4 mb-4"
          icon="mdi-robot-off-outline"
        >
          <div class="text-body-2">
            <span class="font-weight-medium">{{ $t('qaJobsSelfTestReasonLabel') }}</span> {{ status.llmSelfTestReason }}
          </div>
        </v-alert>
      </v-card>

      <!--
        สัญญาสองฝั่งไม่ตรงกัน — ฝั่งโน้นสัญญาว่าส่งครบหกตัวเสมอ
        ⚠️ ต้องขึ้นป้าย ไม่ใช่เติมแถวที่ขาดให้ดูครบ · ตารางที่ครบเสมอจะกลบวันที่ฝั่งโน้น
           เปลี่ยนสัญญา แล้วไม่มีใครรู้ว่ามีงานตัวหนึ่งหายไปจากการมองเห็นตั้งแต่เมื่อไหร่
      -->
      <v-alert v-if="drift" type="warning" variant="tonal" density="compact" class="mt-3" icon="mdi-alert-outline">
        <span class="text-body-2">{{ drift }}</span>
      </v-alert>

      <!-- ══ ตารางงานทั้งหก ═══════════════════════════════════════════════════════ -->
      <v-card class="qa-card mt-3" variant="flat">
        <v-card-title class="d-flex align-center py-3">
          <span class="text-subtitle-1">{{ $t('qaJobsTableTitle') }}</span>
          <v-spacer />
          <span v-if="result.endpoint" class="text-caption text-medium-emphasis">
            {{ $t('qaJobsReadFrom', { endpoint: result.endpoint }) }}
          </span>
        </v-card-title>
        <v-divider />
        <div class="qa-table-wrap pt-3">
          <v-data-table
            :headers="headers"
            :items="rows"
            :items-per-page="-1"
            hide-default-footer
            class="elevation-0 my-data-table qa-table qa-table-wide"
            density="comfortable"
            fixed-header
          >
            <template #[`item.job`]="{ item }">
              <div class="font-weight-medium">{{ item.label }}</div>
              <div class="text-caption text-medium-emphasis">{{ item.job }}</div>
              <div v-if="item.isUnknown" class="text-caption text-warning">{{ $t('qaJobsUnknownJob') }}</div>
            </template>

            <!--
              ⚠️ registered=false ต้องอ่านว่า "ยังไม่มีใครรอของ" ไม่ใช่ "พัง" — จึงเป็นสีเทา
                 ไม่ใช่สีแดง · idle กับ ไม่ได้ลงทะเบียน เป็นคนละเรื่อง: idle = ลงทะเบียนแล้ว
                 และกำลังรอของ ส่วน registered=false = ไม่มี worker อยู่เลย งานจะไม่เดิน
                 จนกว่าจะเปิดสวิตช์ฝั่ง Fusion.Middleware แล้วรีสตาร์ท
            -->
            <template #[`item.state`]="{ item }">
              <div :title="item.stateTitle">
                <v-chip :color="item.stateColor" size="small" variant="tonal" label class="font-weight-medium">
                  <v-icon start size="14">{{ item.stateIcon }}</v-icon>{{ item.stateLabel }}
                </v-chip>
                <div v-if="item.stateNote" class="text-caption text-medium-emphasis mt-1">{{ item.stateNote }}</div>
              </div>
            </template>

            <template #[`item.currentItem`]="{ item }">
              <span v-if="item.currentItem" class="text-body-2">{{ item.currentItem }}</span>
              <span v-else class="text-medium-emphasis">—</span>
            </template>

            <template #[`item.startedAt`]="{ item }">
              <div v-if="item.startedAt">
                <div class="text-body-2">{{ relative(item.startedAt) }}</div>
                <div class="text-caption text-medium-emphasis">{{ formatDateTime(item.startedAt) }}</div>
              </div>
              <span v-else class="text-medium-emphasis" :title="$t('qaJobsNoStartedHint')">—</span>
            </template>

            <template #[`item.lastFinishedAt`]="{ item }">
              <div v-if="item.lastFinishedAt">
                <div class="text-body-2">{{ relative(item.lastFinishedAt) }}</div>
                <div class="text-caption text-medium-emphasis">{{ formatDateTime(item.lastFinishedAt) }}</div>
              </div>
              <span v-else class="text-medium-emphasis" :title="$t('qaJobsNeverRanHint')">—</span>
            </template>

            <template #[`item.lastDurationMs`]="{ item }">
              <span class="qa-num">{{ formatDurationMs(item.lastDurationMs) }}</span>
            </template>

            <!--
              ⚠️ 0 คือ "วันนี้ยังไม่ได้ทำสักชิ้น" ซึ่งเป็นค่าจริง ไม่ใช่ช่องว่าง — ห้ามแทนด้วย "—"
              ⚠️ ตั้งแต่ฝั่ง Fusion.Middleware แก้แล้ว เลขนี้คือ **จำนวนชิ้นงานจริง** ไม่ใช่
                 จำนวนรอบที่วนหาของอีกต่อไป · ของเดิมนับรอบ ทำให้ระบบที่ว่างสนิทดูเหมือน
                 ทำงานอยู่ทั้งวัน — อ่านคู่กับคอลัมน์ "รอบที่วนวันนี้" ที่อยู่ถัดไปเสมอ
            -->
            <template #[`item.processedToday`]="{ item }">
              <span class="qa-num" :title="$t('qaJobsProcessedHint')">{{ item.processedToday ?? 0 }}</span>
            </template>

            <!--
              ⚠️ คอลัมน์นี้คือ "worker ยังหายใจอยู่ไหม" ไม่ใช่ "ทำงานไปเท่าไหร่" ·
                 แถวที่อ่านว่า 0 ชิ้น + 29 รอบ = ปกติดีแต่ไม่มีของให้ทำ ไม่ใช่ปัญหา
              ⚠️ ค่านี้อาจยังไม่มี (middleware รุ่นที่รันอยู่ยังไม่ส่ง pollRoundsToday มา
                 หรือ Chat Framework ยังไม่มีช่องนี้ใน QaMiddlewareJobStatus) — ต้องขึ้น
                 "—" ไม่ใช่ 0 · 0 แปลว่า "วันนี้ worker ไม่ได้วนสักรอบ" ซึ่งเป็นข่าวร้าย
                 คนละเรื่องกับ "รุ่นนี้ยังไม่รายงานค่านี้" ที่ไม่ใช่ปัญหาอะไรเลย
            -->
            <template #[`item.pollRoundsToday`]="{ item }">
              <span v-if="item.pollRoundsToday == null" class="text-medium-emphasis" :title="$t('qaJobsRoundsUnavailableHint')">—</span>
              <span v-else class="qa-num" :title="$t('qaJobsRoundsHint')">{{ item.pollRoundsToday }}</span>
            </template>

            <!--
              ⚠️ "ไม่มี lastError" ไม่ได้แปลว่า "รอบล่าสุดสำเร็จ" เสมอไป — งานที่ยังไม่เคยรัน
                 ก็ไม่มี error เหมือนกัน · ติดป้ายว่าสำเร็จให้มันคือการรายงานความสำเร็จที่ไม่เคยเกิดขึ้น
            -->
            <template #[`item.lastError`]="{ item }">
              <div v-if="item.lastError" class="text-error text-body-2 qa-error-cell" :title="item.lastError">
                <v-icon size="14" color="error" class="mr-1">mdi-alert-circle-outline</v-icon>{{ item.lastError }}
              </div>
              <span v-else-if="item.lastFinishedAt" class="text-success text-body-2">
                <v-icon size="14" color="success" class="mr-1">mdi-check</v-icon>{{ $t('qaJobsSucceeded') }}
              </span>
              <span v-else class="text-medium-emphasis">{{ $t('qaJobsNeverRan') }}</span>
            </template>
          </v-data-table>
        </div>

        <!-- ข้อความ error เต็ม ๆ — ในตารางถูกตัดบรรทัดเดียว แต่คนที่มาหน้านี้คือคนที่ต้องอ่านมันทั้งก้อน -->
        <template v-if="failedJobs.length">
          <v-divider />
          <div class="pa-4">
            <div class="text-subtitle-2 mb-2">{{ $t('qaJobsLastErrorsTitle') }}</div>
            <v-alert
              v-for="job in failedJobs"
              :key="job.job"
              type="error"
              variant="tonal"
              density="compact"
              class="mb-2"
              icon="mdi-alert-circle-outline"
            >
              <div class="text-body-2 font-weight-medium">{{ job.label }} ({{ job.job }})</div>
              <div class="text-body-2">{{ job.lastError }}</div>
              <div v-if="job.lastFinishedAt" class="text-caption text-medium-emphasis mt-1">
                {{ $t('qaJobsRoundFinishedAt', { time: formatDateTime(job.lastFinishedAt) }) }}
              </div>
            </v-alert>
            <!-- ⚠️ error ของรอบล่าสุดไม่ได้แปลว่างานหยุด — รอบถัดไปอาจสำเร็จไปแล้วก็ได้ -->
            <div class="text-caption text-medium-emphasis">
              <span class="font-weight-medium">{{ $t('qaJobsErrorScopeStrong') }}</span> {{ $t('qaJobsErrorScopeNote') }}
            </div>
          </div>
        </template>
      </v-card>

      <div class="text-caption text-medium-emphasis mt-3">
        {{ $t('qaJobsReadOnlyNote', { updated: lastUpdatedText }) }}
      </div>
    </template>
  </QaPageShell>
</template>

<script>
/**
 * งานเบื้องหลัง QA — หน้าดูสถานะ worker ทั้งหกของ Fusion.Middleware
 *
 * งานหกตัว (classification / recommend / nba / sentiment / evaluation / summary)
 * ถูกย้ายออกจากรีโปนี้ไปอยู่ service แยกชื่อ **Fusion.Middleware** ซึ่งใช้ฐานข้อมูล
 * ตัวเดียวกันแต่**ไม่มีหน้าเว็บของตัวเองโดยตั้งใจ** · หน้านี้จึงเป็นตาเดียวที่ทีมมี
 * ไว้ดูว่างานยังเดินอยู่จริงไหม
 *
 * 🔒 ⚠️ Api-Key ของ Fusion.Middleware **ไม่เคยมาถึงเบราว์เซอร์** · หน้านี้ยิงไปที่
 *    `api/v1/QaConsole/middleware/jobs` ของ Chat Framework ด้วยคุกกี้ล็อกอินปกติ
 *    แล้ว Chat Framework เป็นฝ่ายถือคีย์ไปคุยกับ middleware ฝั่ง server
 *    ห้ามเปลี่ยนให้หน้านี้ยิงตรงไปที่ middleware ไม่ว่าด้วยเหตุผลใด
 *    🔒 และห้ามใส่ค่าคีย์/URL ลงในข้อความแปลใน plugins/i18n.js เด็ดขาด — ค่าที่ต้อง
 *       โผล่บนหน้าจอ (endpoint / health url) ส่งเข้าไปเป็นพารามิเตอร์ตอน runtime เท่านั้น
 *
 * ⚠️ เหตุผลที่หน้านี้ต้องพูดเยอะกว่าตารางเปล่า ๆ: อาการที่พบบ่อยที่สุดคือ
 *    "เปิดหน้ามาแล้วทุกอย่างว่าง" ซึ่งมีสาเหตุที่ต่างกันคนละเรื่องอย่างน้อยห้าแบบ —
 *    ยังไม่ได้ตั้งค่า / ต่อไม่ติด / คีย์ผิด / สวิตช์ใหญ่ปิด / ถูกกลั้นเพราะการตรวจ LLM
 *    ไม่ผ่าน — และแบบที่หกคือ "ปกติดี แค่ไม่มีของให้ทำ" · ถ้าหน้าจอไม่แยกให้
 *    คนจะไปแก้ผิดจุดทุกครั้ง และครั้งที่แพงที่สุดคือไปเปิดสวิตช์ที่เปิดอยู่แล้ว
 *    ⚠️ หกแบบนี้ต้องแยกกันให้ขาดทั้งภาษาไทยและอังกฤษ — โดยเฉพาะ "ถูกกลั้นเพราะ
 *       การตรวจ LLM" กับ "สวิตช์ใหญ่ปิด" ที่หน้าตาบนจอเหมือนกันเป๊ะ (ทุกงานว่าง)
 *       แต่แก้คนละที่คนละคน · ห้ามยุบสองอันนี้เป็นคำว่า "ไม่ทำงาน" คำเดียวในภาษาใดก็ตาม
 */
import qaConsole from '@/services/qaConsole';
import { formatDateTime } from '@/utils/qaFormat';
import QaPageShell from '@/components/QAConsole/QaPageShell.vue';

/** รอบการถามซ้ำ — งานเบื้องหลังหนึ่งรอบใช้เวลาระดับวินาที 5 วิจึงเห็นการเปลี่ยนแปลงทัน */
const REFRESH_MS = 5000;

/** นาฬิกาของหน้าจอ — มีไว้ให้ "เริ่มมาแล้ว 12 วินาที" เดินจริง ไม่ใช่ค้างจนกว่าจะ poll รอบหน้า */
const CLOCK_MS = 1000;

/**
 * ชื่อของงานแต่ละตัว — เก็บเป็น**คีย์ i18n** ไม่ใช่ข้อความ
 *
 * ⚠️ ตั้งให้ตรงกับชื่อแท็บในหน้า QA Settings โดยตั้งใจ — คนที่เห็นว่า "งาน sentiment
 *    ไม่เดิน" จะได้รู้ทันทีว่าไปดูค่าตั้งที่แท็บไหน · ถ้าเปลี่ยนชื่อแท็บฝั่งโน้น
 *    ให้เปลี่ยนค่าของคีย์เหล่านี้ด้วย ไม่งั้นสองหน้าจะเรียกของเดียวกันคนละชื่อ
 *    (แท็บฝั่งนั้นยังฮาร์ดโค้ดไทยอยู่ — ตอนย้ายขึ้น i18n ให้ใช้ค่าชุดเดียวกันนี้)
 */
const JOB_LABEL_KEYS = {
  classification: 'qaJobsNameClassification',
  recommend: 'qaJobsNameRecommend',
  nba: 'qaJobsNameNba',
  sentiment: 'qaJobsNameSentiment',
  evaluation: 'qaJobsNameEvaluation',
  summary: 'qaJobsNameSummary',
};

/** ลำดับตามสัญญาของ Fusion.Middleware — ใช้ตรวจว่ายังครบหกตัวไหม ไม่ได้ใช้เรียงใหม่ */
const JOB_CONTRACT_ORDER = ['classification', 'recommend', 'nba', 'sentiment', 'evaluation', 'summary'];

export default {
  name: 'QAJobs',
  components: { QaPageShell },
  data() {
    return {
      loading: true,
      inFlight: false,
      /** error ที่ทำให้ไม่มีอะไรให้แสดงเลย (โหลดครั้งแรกล้ม) — หน้าจะเป็นการ์ดลองใหม่ */
      error: null,
      /** error ที่เกิดตอนมีของเดิมอยู่แล้ว — ขึ้นป้ายว่าของที่เห็นเป็นของเก่า ไม่ล้างจอ */
      staleError: null,
      result: null,
      autoRefresh: true,
      lastUpdatedAt: null,
      now: Date.now(),
      poll: null,
      clock: null,
      refreshSeconds: Math.round(REFRESH_MS / 1000),
    };
  },
  computed: {
    /** ⚠️ แตะได้เฉพาะเมื่อ result.isOk — ชั้น service คืน null เมื่ออ่านไม่ได้ ไม่ใช่ค่าเริ่มต้นปลอม */
    status() {
      return (this.result && this.result.status) || {};
    },
    jobs() {
      return (this.result && this.result.jobs) || [];
    },
    /** แถวของตาราง — ⚠️ เรียงตามที่ได้รับมาจริง ไม่ sort ใหม่ (ดูคอมเมนต์ที่ headers) */
    rows() {
      return this.jobs.map((job) => {
        const labelKey = JOB_LABEL_KEYS[job.job];
        return {
          ...job,
          label: labelKey ? this.$t(labelKey) : job.job,
          isUnknown: !labelKey,
          pollRoundsToday: this.pollRoundsOf(job),
          ...this.stateOf(job),
        };
      });
    },
    runningJobs() {
      return this.rows.filter((j) => j.registered && j.state === 'running');
    },
    failedJobs() {
      return this.rows.filter((j) => j.lastError);
    },
    registeredCount() {
      return this.jobs.filter((j) => j.registered).length;
    },
    headers() {
      // ⚠️ ปิด sortable ทุกคอลัมน์โดยตั้งใจ — ลำดับหกตัวนี้คือลำดับของ pipeline ตามสัญญา
      //    ฝั่ง Fusion.Middleware · ให้คนสลับลำดับได้เมื่อไหร่ ตาที่จำตำแหน่งไว้จะอ่านผิดแถว
      //    และวันที่ฝั่งโน้นส่งมาไม่ครบ/สลับลำดับ จะไม่มีใครสังเกตเห็น
      return [
        { title: this.$t('qaJobsColJob'), key: 'job', sortable: false, minWidth: '210px' },
        { title: this.$t('qaJobsColState'), key: 'state', sortable: false, minWidth: '170px' },
        { title: this.$t('qaJobsColCurrent'), key: 'currentItem', sortable: false, minWidth: '160px' },
        { title: this.$t('qaJobsColStarted'), key: 'startedAt', sortable: false, minWidth: '150px' },
        { title: this.$t('qaJobsColFinished'), key: 'lastFinishedAt', sortable: false, minWidth: '150px' },
        { title: this.$t('qaJobsColDuration'), key: 'lastDurationMs', align: 'end', sortable: false, width: '140px' },
        // ⚠️ สองคอลัมน์นี้ต้องอยู่ติดกันและหัวคอลัมน์ต้องอ่านต่างกันชัด ๆ ทั้งสองภาษา:
        //    "ชิ้นที่ทำวันนี้" = งานที่ทำจริง · "รอบที่วนวันนี้" = worker ยังหายใจอยู่ไหม
        //    แถวที่อ่านว่า 0 ชิ้น + 29 รอบ ต้องอ่านออกทันทีว่า "ปกติ แค่ไม่มีของให้ทำ"
        { title: this.$t('qaJobsColProcessed'), key: 'processedToday', align: 'end', sortable: false, width: '110px' },
        { title: this.$t('qaJobsColRounds'), key: 'pollRoundsToday', align: 'end', sortable: false, width: '120px' },
        { title: this.$t('qaJobsColResult'), key: 'lastError', sortable: false, minWidth: '240px' },
      ];
    },
    /** ป้ายสถานะการตรวจ LLM (off / checking / failed / passed) */
    selfTest() {
      const byState = {
        off: { label: this.$t('qaJobsSelfTestOff'), tone: 'grey', icon: 'mdi-minus-circle-outline' },
        checking: { label: this.$t('qaJobsSelfTestChecking'), tone: 'info', icon: 'mdi-progress-clock' },
        failed: { label: this.$t('qaJobsSelfTestFailed'), tone: 'error', icon: 'mdi-robot-off-outline' },
        passed: { label: this.$t('qaJobsSelfTestPassed'), tone: 'success', icon: 'mdi-robot-happy-outline' },
      };
      // ⚠️ ค่าที่ไม่รู้จักให้โชว์ค่าดิบที่ฝั่งโน้นส่งมา ไม่ใช่กลบด้วยคำว่า "ไม่ทราบ" —
      //    สถานะชนิดใหม่ที่เพิ่งเพิ่มฝั่ง middleware ต้องมองเห็นได้ ไม่ใช่หายไปเงียบ ๆ
      return (
        byState[this.status.llmSelfTestState] || {
          label: this.status.llmSelfTestState || this.$t('qaJobsSelfTestUnknown'),
          tone: 'grey',
          icon: 'mdi-help-circle-outline',
        }
      );
    },
    /**
     * คำตัดสินรวมของทั้งหน้า — เรียงตามลำดับ "สาเหตุที่ใหญ่กว่าบังสาเหตุที่เล็กกว่า"
     *
     * ⚠️ ลำดับสำคัญ: สวิตช์ใหญ่ปิด ต้องมาก่อนเรื่องการกลั้นงาน เพราะสวิตช์ปิดแล้ว
     *    ไม่มี worker ให้กลั้นตั้งแต่แรก · และ "ว่างเพราะไม่มีของ" ต้องเป็นอันสุดท้าย
     *    เสมอ — มันเป็นข้อสรุปที่ถูกต่อเมื่อไม่มีสาเหตุอื่นเหลืออยู่แล้วเท่านั้น
     */
    verdict() {
      const s = this.status;

      // การตรวจ LLM ไม่ผ่าน แต่ฝั่งโน้น**ไม่ได้**กลั้นงานไว้ — ไม่ใช่สาเหตุที่งานไม่เดิน
      // จึงไม่ควรไปแย่งหัวข้อหลัก แต่ก็ห้ามเงียบ: งานยังเดินต่อทั้งที่ LLM อาจใช้ไม่ได้
      // แปลว่าผลที่ออกมาในช่วงนี้อาจไม่น่าเชื่อถือ ซึ่งเป็นคนละปัญหาที่ก็ต้องมีคนรู้
      const selfTestFailedButNotHolding = s.llmSelfTestState === 'failed' && !s.jobsHeldByLlmSelfTest;
      const looseSelfTestHint = this.$t('qaJobsSelfTestLooseHint');

      if (!s.jobsEnabled) {
        return {
          tone: 'error',
          icon: 'mdi-toggle-switch-off-outline',
          title: this.$t('qaJobsVerdictSwitchOffTitle'),
          body: this.$t('qaJobsVerdictSwitchOffBody'),
          hint: this.$t('qaJobsVerdictSwitchOffHint'),
        };
      }

      // สวิตช์เปิดแต่ไม่มีใครลงทะเบียน — ค่าที่ขัดกันเองแบบนี้เกือบทุกครั้งแปลว่า
      // ฝั่งโน้นยังไม่ได้รีสตาร์ทหลังเปลี่ยนค่า (worker ลงทะเบียนตอน process ขึ้นเท่านั้น)
      if (this.jobs.length && this.registeredCount === 0) {
        return {
          tone: 'warning',
          icon: 'mdi-restart-alert',
          title: this.$t('qaJobsVerdictNoWorkerTitle'),
          body: this.$t('qaJobsVerdictNoWorkerBody'),
          hint: '',
        };
      }

      if (s.jobsHeldByLlmSelfTest) {
        const checking = s.llmSelfTestState === 'checking';
        return {
          tone: checking ? 'info' : 'error',
          icon: checking ? 'mdi-progress-clock' : 'mdi-hand-back-left-outline',
          title: checking ? this.$t('qaJobsVerdictCheckingTitle') : this.$t('qaJobsVerdictHeldTitle'),
          // ⚠️ ประโยคนี้คือหัวใจของหน้านี้ · "ทุกงานว่าง" ตรงนี้ไม่ได้แปลว่าไม่มีของให้ทำ
          body: this.$t('qaJobsVerdictHeldBody'),
          hint: checking ? this.$t('qaJobsVerdictCheckingHint') : this.$t('qaJobsVerdictHeldHint'),
        };
      }

      if (this.runningJobs.length) {
        return {
          tone: selfTestFailedButNotHolding ? 'warning' : 'success',
          icon: 'mdi-cog-play-outline',
          title: this.$t('qaJobsVerdictRunningTitle', { n: this.runningJobs.length }),
          body: this.$t('qaJobsVerdictRunningBody', { list: this.runningJobs.map((j) => j.label).join(' · ') }),
          hint: selfTestFailedButNotHolding ? looseSelfTestHint : '',
        };
      }

      return {
        tone: selfTestFailedButNotHolding ? 'warning' : 'success',
        icon: 'mdi-check-circle-outline',
        title: this.$t('qaJobsVerdictIdleTitle'),
        body: this.$t('qaJobsVerdictIdleBody'),
        hint: selfTestFailedButNotHolding ? looseSelfTestHint : this.$t('qaJobsVerdictIdleHint'),
      };
    },
    /** ฝั่งโน้นสัญญาว่าส่งครบหกตัวเสมอ — ไม่ครบ/ไม่รู้จัก = ต้องเห็น ไม่ใช่ซ่อน */
    drift() {
      if (!this.result || !this.result.isOk) return '';
      const missing = JOB_CONTRACT_ORDER.filter((name) => !this.jobs.some((j) => j.job === name));
      const unknown = this.rows.filter((r) => r.isUnknown).map((r) => r.job);
      const parts = [];
      if (missing.length) parts.push(this.$t('qaJobsDriftMissing', { list: missing.join(', ') }));
      if (unknown.length) parts.push(this.$t('qaJobsDriftUnknown', { list: unknown.join(', ') }));
      if (!parts.length) return '';
      return this.$t('qaJobsDrift', { parts: parts.join(' · ') });
    },
    /**
     * การ์ดอธิบายเมื่ออ่านสถานะไม่ได้ — คนละข้อความ คนละสิ่งที่ต้องไปทำ ต่อหนึ่งสาเหตุ
     *
     * ⚠️ ห้ามยุบเป็นกล่องแดงกล่องเดียว · "ยังไม่ได้ตั้งค่า" กับ "คีย์ผิด" กับ "ต่อไม่ติด"
     *    เป็นคนละงานของคนละคน · กล่องแดงที่บอกแค่ว่า "ผิดพลาด" ทำให้ทุกคนเริ่มจากศูนย์ทุกครั้ง
     */
    outcomeCard() {
      const O = qaConsole.MIDDLEWARE_OUTCOME;
      const code = this.result ? this.result.httpStatusCode : null;
      const health = this.healthUrl;

      const cards = {
        [O.NOT_CONFIGURED]: {
          tone: 'warning',
          icon: 'mdi-cog-off-outline',
          title: this.$t('qaJobsNotConfiguredTitle'),
          lead: this.$t('qaJobsNotConfiguredLead'),
          todo: [
            this.$t('qaJobsNotConfiguredTodo1'),
            this.$t('qaJobsNotConfiguredTodo2'),
            this.$t('qaJobsNotConfiguredTodo3'),
            this.$t('qaJobsNotConfiguredTodo4'),
            this.$t('qaJobsNotConfiguredTodo5'),
          ],
        },
        [O.UNREACHABLE]: {
          tone: 'error',
          icon: 'mdi-lan-disconnect',
          title: this.$t('qaJobsUnreachableTitle'),
          lead: this.$t('qaJobsUnreachableLead'),
          todo: [
            // ⚠️ url ถูกส่งเข้าไปเป็นพารามิเตอร์ ไม่ได้เขียนไว้ในข้อความแปล — ข้อความแปล
            //    ต้องไม่มีที่อยู่ของ service ฝังอยู่ (คนละ environment คนละที่อยู่)
            health ? this.$t('qaJobsUnreachableTodoHealth', { url: health }) : this.$t('qaJobsUnreachableTodoNoHealth'),
            this.$t('qaJobsUnreachableTodoBaseUrl'),
            this.$t('qaJobsUnreachableTodoNetwork'),
          ],
        },
        [O.UNAUTHORIZED]: {
          tone: 'error',
          icon: 'mdi-key-alert-outline',
          title: this.$t('qaJobsUnauthorizedTitle'),
          lead: this.$t('qaJobsUnauthorizedLead'),
          todo: [
            this.$t('qaJobsUnauthorizedTodo1'),
            this.$t('qaJobsUnauthorizedTodo2'),
            this.$t('qaJobsUnauthorizedTodo3'),
            this.$t('qaJobsUnauthorizedTodo4'),
          ],
        },
        [O.HTTP_ERROR]: {
          tone: 'error',
          icon: 'mdi-server-network-off',
          title: this.$t('qaJobsHttpErrorTitle', { code: code ?? this.$t('qaJobsHttpUnknownCode') }),
          lead: this.$t('qaJobsHttpErrorLead'),
          todo: [this.$t('qaJobsHttpErrorTodo403'), this.$t('qaJobsHttpErrorTodo404'), this.$t('qaJobsHttpErrorTodo5xx')],
        },
        [O.BAD_RESPONSE]: {
          tone: 'warning',
          icon: 'mdi-file-question-outline',
          title: this.$t('qaJobsBadResponseTitle'),
          lead: this.$t('qaJobsBadResponseLead'),
          todo: [this.$t('qaJobsBadResponseTodo1'), this.$t('qaJobsBadResponseTodo2')],
        },
        [O.UNEXPECTED]: {
          tone: 'error',
          icon: 'mdi-help-circle-outline',
          title: this.$t('qaJobsUnexpectedTitle'),
          lead: this.$t('qaJobsUnexpectedLead'),
          todo: [this.$t('qaJobsUnexpectedTodo1')],
        },
      };

      return cards[this.result && this.result.outcome] || cards[O.UNEXPECTED];
    },
    /**
     * เส้น health ของ Fusion.Middleware — เปิดสาธารณะ ไม่ต้องใช้คีย์
     *
     * ⚠️ ประกอบจาก origin ของ endpoint ที่ backend ส่งมา (ซึ่งถูกตัด userinfo/query ทิ้งแล้ว)
     *    ไม่ใช่จากค่า config ที่ส่งลงมาเอง — หน้าเว็บไม่เคยเห็น BaseUrl หรือ ApiKey
     *    แสดงเป็นข้อความให้ก๊อปไปเปิดเอง ไม่ทำเป็นลิงก์: host นี้มักอยู่หลัง network
     *    ที่เบราว์เซอร์ของคนดูเข้าไม่ถึง ลิงก์ที่กดแล้วไม่ไปไหนจะกลายเป็นเบาะแสผิด
     */
    healthUrl() {
      const raw = this.result && this.result.endpoint;
      if (!raw) return '';
      try {
        return `${new URL(raw).origin}/api/mw/v1/health`;
      } catch {
        return '';
      }
    },
    lastUpdatedRelative() {
      if (!this.lastUpdatedAt) return this.$t('qaJobsNeverReadShort');
      return this.$t('qaJobsAgo', { d: this.relativeFrom(this.lastUpdatedAt) });
    },
    lastUpdatedText() {
      if (!this.lastUpdatedAt) return this.$t('qaJobsNeverRead');
      return this.$t('qaJobsUpdatedAt', { when: this.lastUpdatedRelative });
    },
  },
  watch: {
    // "หยุดอัตโนมัติ" ต้องหยุดยิงจริง ๆ ไม่ใช่ยิงแล้วทิ้งผล — คนกดปุ่มนี้ตอนกำลังอ่าน
    // ข้อความ error ยาว ๆ อยู่ และไม่อยากให้ตารางขยับหนีกลางประโยค
    autoRefresh(on) {
      if (on) this.startPoll();
      else this.stopPoll();
    },
  },
  mounted() {
    this.load();
    this.startClock();
    this.startPoll();
  },
  /**
   * ⚠️ ต้องหยุดตัวจับเวลาทั้งสองตัวตอนออกจากหน้า ไม่งั้นมันไล่ถาม API ต่อไปเรื่อย ๆ
   *    ทั้งที่ไม่มีใครดู และจะซ้อนกันหลายตัวถ้าเข้า-ออกหน้านี้หลายรอบ
   */
  beforeUnmount() {
    this.stopPoll();
    this.stopClock();
  },
  methods: {
    formatDateTime,

    /**
     * @param quiet true = โหลดซ้ำเงียบ ๆ ไม่สลับไป skeleton
     * ⚠️ เทียบ === true เพราะเมธอดนี้ผูกกับ @retry ของ QaPageShell ซึ่งอาจส่ง argument อื่นมา
     */
    async load(quiet = false) {
      // กันคำขอซ้อนกัน — รอบ poll กับปุ่มรีเฟรชกดชนกันได้ และ response ที่มาสลับลำดับ
      // จะทำให้หน้าจอเด้งกลับไปแสดงค่าที่เก่ากว่า
      if (this.inFlight) return;
      this.inFlight = true;
      if (quiet !== true) this.loading = true;

      try {
        this.result = await qaConsole.getMiddlewareJobs();
        this.lastUpdatedAt = Date.now();
        this.error = null;
        this.staleError = null;
      } catch (err) {
        // ⚠️ กรณีที่มาลงตรงนี้ไม่ใช่แค่ "เน็ตหลุด" — ถ้า Chat Framework เองรอ middleware
        //    เกินเส้นตายของมัน (QaConsoleController.MiddlewareStatusDeadline) มันจะตอบ
        //    200 + APIStatus.Error พร้อมประโยคไทยที่อธิบายไว้แล้ว ซึ่งชั้น service แปลงเป็น
        //    exception · เคสนั้นจึงขึ้นเป็นการ์ด error ของ QaPageShell ไม่ใช่การ์ดแยกสาเหตุ
        //    ข้างบน — ตั้งใจ เพราะข้อความที่ backend ส่งมาบอกความจริงครบอยู่แล้ว
        const friendly = this.friendlyError(err);
        // ⚠️ มีของเดิมอยู่แล้วห้ามล้างจอ — ขึ้นป้ายว่าเป็นของเก่าแทน (ดูแบนเนอร์บนสุด)
        if (this.result) this.staleError = friendly;
        else this.error = friendly;
      } finally {
        this.loading = false;
        this.inFlight = false;
      }
    },

    /**
     * แปลง error ของ "เส้นทางฝั่งเราเอง" ให้เป็นภาษาที่บอกว่าต้องไปแก้ที่ไหน
     *
     * ⚠️ 401/403 ตรงนี้คือ **Chat Framework ปฏิเสธผู้ใช้คนนี้** (ทะเบียนสิทธิ์ใน APIAuthen
     *    เปิดเส้นนี้ให้แค่ role 0,1) — คนละเรื่องกันโดยสิ้นเชิงกับ 401 ที่ Fusion.Middleware
     *    ตอบกลับมา ซึ่งแปลว่า Api-Key ผิด · สองอันนี้แก้คนละที่ คนละคนแก้ ถ้าหน้าจอเล่ารวมกัน
     *    จะมีคนไปไล่เปลี่ยนคีย์ทั้งที่ปัญหาคือบัญชีตัวเองไม่มีสิทธิ์
     */
    friendlyError(err) {
      const code = err && err.httpStatus;
      if (code === 401 || code === 403) {
        return new Error(this.$t('qaJobsForbidden'));
      }
      return err;
    },

    startPoll() {
      if (this.poll) return;
      this.poll = setInterval(() => {
        // แท็บที่ถูกซ่อนอยู่ไม่ต้องถาม — ไม่มีใครดู และเบราว์เซอร์หน่วง timer อยู่แล้ว
        // (ไม่ได้หยุด timer เพราะกลับมาเปิดแท็บแล้วต้องได้ค่าใหม่ในไม่กี่วินาที ไม่ต้องกดเอง)
        if (typeof document !== 'undefined' && document.hidden) return;
        this.load(true);
      }, REFRESH_MS);
    },
    stopPoll() {
      if (!this.poll) return;
      clearInterval(this.poll);
      this.poll = null;
    },
    startClock() {
      if (this.clock) return;
      this.clock = setInterval(() => {
        this.now = Date.now();
      }, CLOCK_MS);
    },
    stopClock() {
      if (!this.clock) return;
      clearInterval(this.clock);
      this.clock = null;
    },

    /**
     * จำนวน "รอบที่วนหาของ" วันนี้ของ worker ตัวหนึ่ง · null = ยังไม่มีค่าให้ดู
     *
     * ⚠️ คนละเรื่องกับ processedToday: processedToday = จำนวนชิ้นงานที่ทำจริง ส่วนตัวนี้
     *    = จำนวนรอบที่ worker ตื่นมาวนหาของ · แถวที่อ่านว่า "0 ชิ้น · 29 รอบ" แปลว่า
     *    ปกติดีแต่ไม่มีของให้ทำ ไม่ใช่ระบบค้าง (ของเดิม processedToday นับรอบอยู่
     *    ทำให้ระบบที่ว่างสนิทดูเหมือนทำงานทั้งวัน — นั่นคือบั๊กที่ทำให้คอลัมน์นี้เกิดขึ้น)
     *
     * ⚠️ คืน null เมื่อฝั่งโน้นยังไม่ส่งค่านี้มา (middleware รุ่นเก่า หรือ Chat Framework
     *    ยังไม่มีช่อง pollRoundsToday ใน QaMiddlewareJobStatus) — หน้าจอจะขึ้น "—"
     *    ห้ามแทนด้วย 0 เด็ดขาด: 0 = "วันนี้ worker ไม่ได้วนสักรอบ" ซึ่งแปลว่าตายอยู่
     *    ส่วน "—" = "รุ่นนี้ยังไม่รายงาน" ซึ่งไม่ได้บอกอะไรเกี่ยวกับสุขภาพของ worker เลย
     */
    pollRoundsOf(job) {
      const raw = job && job.pollRoundsToday;
      if (raw == null) return null;
      const n = Number(raw);
      return Number.isFinite(n) ? n : null;
    },

    /**
     * ป้ายสถานะของงานหนึ่งตัว
     *
     * ⚠️ สามสถานะนี้ต้องแยกกันให้ขาด และ "ไม่ได้ลงทะเบียน" ต้องไม่หน้าตาเหมือนความผิดพลาด:
     *    - registered=false → worker ไม่ได้ลงทะเบียน (Jobs:Enabled=false) · สีเทา
     *    - running          → กำลังทำงาน · สีเขียว
     *    - idle             → ลงทะเบียนแล้วและกำลังรอของ · สีเทา (ไม่ใช่สีแดง ไม่ใช่คำเตือน)
     */
    stateOf(job) {
      if (!job.registered) {
        return {
          stateLabel: this.$t('qaJobsStateUnregistered'),
          stateColor: 'grey',
          stateIcon: 'mdi-power-plug-off-outline',
          stateNote: this.$t('qaJobsStateUnregisteredNote'),
          stateTitle: this.$t('qaJobsStateUnregisteredTitle'),
        };
      }
      if (job.state === 'running') {
        return {
          stateLabel: this.$t('qaJobsStateRunning'),
          stateColor: 'success',
          stateIcon: 'mdi-play-circle-outline',
          stateNote: '',
          stateTitle: this.$t('qaJobsStateRunningTitle'),
        };
      }
      return {
        stateLabel: this.$t('qaJobsStateIdle'),
        stateColor: 'grey',
        stateIcon: 'mdi-pause-circle-outline',
        // ⚠️ ความต่างที่คนพลาดบ่อยที่สุด: "ว่าง" ตอนถูกกลั้นไว้ ไม่ได้แปลว่าไม่มีของให้ทำ
        //    ต้องบอกตรงแถวด้วย ไม่ใช่บอกแค่ที่การ์ดข้างบนแล้วหวังว่าคนจะเลื่อนขึ้นไปอ่าน
        stateNote: this.status.jobsHeldByLlmSelfTest ? this.$t('qaJobsStateHeldNote') : '',
        stateTitle: this.status.jobsHeldByLlmSelfTest ? this.$t('qaJobsStateHeldTitle') : this.$t('qaJobsStateIdleTitle'),
      };
    },

    /**
     * "12 วินาที" / "3 นาที" / "2 ชม." — เทียบกับนาฬิกาของเบราว์เซอร์
     *
     * ⚠️ เวลาที่ได้มาเป็นของนาฬิกาฝั่ง Fusion.Middleware ส่วนตัวหารเป็นนาฬิกาของเครื่องคนดู
     *    สองเครื่องไม่ตรงกันได้เป็นวินาที · ค่าติดลบเล็กน้อยจึงคือนาฬิกาเหลื่อมกัน
     *    ไม่ใช่เวลาในอนาคต — แสดงว่า "เพิ่งเริ่ม" ดีกว่าโชว์เลขติดลบให้คนสงสัยว่าระบบเพี้ยน
     */
    relative(iso) {
      if (!iso) return '—';
      const ms = new Date(iso).getTime();
      if (!Number.isFinite(ms)) return '—';
      if (this.now - ms < 0) return this.$t('qaJobsJustStarted');
      return this.$t('qaJobsAgo', { d: this.relativeFrom(ms) });
    },
    relativeFrom(ms) {
      const diff = Math.max(0, this.now - ms);
      const sec = Math.floor(diff / 1000);
      if (sec < 60) return this.$t('qaJobsDurSeconds', { n: sec });
      const min = Math.floor(sec / 60);
      if (min < 60) return this.$t('qaJobsDurMinutes', { n: min });
      const hr = Math.floor(min / 60);
      if (hr < 24) return this.$t('qaJobsDurHours', { n: hr });
      // ⚠️ ภาษาอังกฤษต้องแยกเอกพจน์/พหูพจน์เอง (ไทยใช้คำเดียว) — "1 days ago" อ่านแล้วสะดุด
      //    บนหน้าจอที่คนมาดูเพราะสงสัยว่าตัวเลขเชื่อได้ไหม
      const day = Math.floor(hr / 24);
      return this.$t(day === 1 ? 'qaJobsDurDay' : 'qaJobsDurDays', { n: day });
    },

    /**
     * มิลลิวินาที → ข้อความที่คนอ่านออก
     * ⚠️ ไม่ใช้ formatDuration() ของ qaFormat — ตัวนั้นรับ "วินาที" และคืนรูป "5:18"
     *    ซึ่งอ่านเป็นความยาวสายสนทนา ไม่ใช่เวลาที่ worker ใช้ต่อรอบซึ่งมักต่ำกว่า 1 วินาที
     */
    formatDurationMs(ms) {
      if (ms == null) return '—';
      if (ms < 1000) return this.$t('qaJobsDurMs', { n: ms });
      if (ms < 60000) return this.$t('qaJobsDurSeconds', { n: (ms / 1000).toFixed(1) });
      const min = Math.floor(ms / 60000);
      const sec = Math.round((ms % 60000) / 1000);
      return this.$t('qaJobsDurMinSec', { m: min, s: sec });
    },
  },
};
</script>

<style scoped>
.qa-todo {
  padding-left: 20px;
}
.qa-todo li {
  margin-bottom: 4px;
}
.qa-code {
  font-size: 12px;
  word-break: break-all;
}
/*
  ข้อความ error ในตารางตัดเหลือบรรทัดเดียว — ข้อความเต็มอยู่ในกล่องใต้ตารางและใน title
  (ปล่อยให้ยาวเต็มจะดันความสูงแถวจนตารางหกแถวอ่านเทียบกันไม่ได้ ซึ่งเป็นเหตุผลเดียว
   ที่คนเปิดหน้านี้)
*/
.qa-error-cell {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 360px;
}
</style>
